<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useDataFormat } from '@/composables/useDataFormat.js';
import { useFormatCurrency } from '@/composables/useFormatCurrency.js';
import { useRoutingButtonActions } from '@/composables/useRoutingButtonActions.js';
import { pakketStore } from '@/stores/pakket.js';
import { verzekeringStore } from '@/stores/verzekering.js';

const { convertToCurrencyFormat } = useFormatCurrency();
const { allInsuranceNamesFormat } = useDataFormat();

const pakket = pakketStore();
const verzekering = verzekeringStore();
const { maatschappij, naam, logo } = storeToRefs(pakket);
const { submitButtonAction } = useRoutingButtonActions();

const showProductProvisie = ref(false);
const chevronUp = new URL('@/assets/svg/icons/chevron-up.svg', import.meta.url).href;

const totalFirstPayment = computed(() => {
  return pakket.totaalpremie * pakket.getGeenneFactor;
});

</script>

<template>
  <div class="summary">
    <FormKit
      id="funnel"
      type="form"
      autocomplete="off"
      :actions="false"
      @submit="submitButtonAction"
    >
      <table class="coverage">
        <tbody>
          <tr>
            <td class="coverage__name">
              {{ maatschappij }}
            </td>
            <td class="coverage__logo">
              <img
                class="img-fluid"
                :src="`${logo}`"
                :alt="maatschappij"
              >
            </td>
          </tr>
          <tr>
            <td class="coverage__type">
              {{ naam }}
            </td>
          </tr>
          <tr>
            <td
              class="py-2"
              colspan="2"
            >
              {{ allInsuranceNamesFormat(verzekering.getAllInsuranceNames()) }}
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <hr>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Alle producten en bijbehorende prijzen -->
      <div
        v-for="(premium, index) in verzekering.verzekering"
        :key="index"
        class="d-flex justify-content-between py-3"
      >
        <div>{{ premium.soort }}</div>

        <div class="fw-bold">
          {{ convertToCurrencyFormat(premium.premie) }}
        </div>
      </div>

      <hr class="strong">

      <!-- Totaalpremie en berekening -->
      <div class="d-flex justify-content-between py-3">
        <div>{{ $t('summary.general.total') }}</div>

        <!-- (Totaal + korting) - kosten. Door de volgorde van de premie opbouw berekening -->
        <div class="fw-bold">
          {{ parseFloat((pakket.totaalpremie + pakket.pakket_korting - pakket.kosten).toFixed(2)) }}
        </div>
      </div>
      <div
        v-if="pakket.pakket_korting > 0"
        class="d-flex justify-content-between py-3"
      >
        <div class="discount">
          {{ $t('summary.voordeelpakket.discount-per-term', { term: pakket.betalingstermijn.toLowerCase() }) }}
        </div>

        <div class="fw-bold">
          {{ $t('general.dash') }} {{ convertToCurrencyFormat(pakket.pakket_korting) }}
        </div>
      </div>
      <div class="d-flex justify-content-between py-3">
        <div>{{ $t('summary.general.service-contribution') }}</div>

        <div class="fw-bold">
          {{ convertToCurrencyFormat(pakket.kosten) }}
        </div>
      </div>

      <hr class="strong">

      <div class="d-flex justify-content-between py-2">
        <div>{{ $t('summary.general.pay-per-term', { term: pakket.betalingstermijn.toLowerCase() }) }}</div>

        <div class="totals__amount">
          {{ convertToCurrencyFormat(pakket.totaalpremie) }}
        </div>
      </div>

      <template v-if="pakket.getGeennee">
        <hr>

        <table>
          <tbody>
            <tr>
              <td>
                <span v-if="pakket.getGeenneFactor === 3">{{ $t('additional-coverage.premiumThreeMonths') }}</span><span v-else>{{ $t('additional-coverage.premium') }}</span>{{ $t('additional-coverage.inclAdditionalCoverages') }}
              </td>
              <td class="text-end">
                {{ convertToCurrencyFormat(pakket.totaalpremie * pakket.getGeenneFactor) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('additional-coverage.firstPayment') }}</td>
              <td class="totals__amount">
                {{ convertToCurrencyFormat(totalFirstPayment) }}
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <hr>

      <small v-if="pakket.getGeennee">{{ $t('summary.general.second-policy-subline') }}</small>

      <small v-else>{{
        $t('summary.general.policy-subline', { provisie: convertToCurrencyFormat(verzekering.getTotalProvisie()) })
      }}</small>

      <div
        v-if="showProductProvisie"
        class="pt-2"
      >
        <div>{{ $t('summary.voordeelpakket.provisie-per-product') }}{{ $t('general.colon') }}</div>

        <div
          v-for="(premium, index) in verzekering.verzekering"
          :key="index"
          class="d-flex justify-content-between py-3"
        >
          <div>{{ premium.soort }}</div>

          <div class="fw-bold">
            {{ convertToCurrencyFormat(premium.provisie) }}
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-center">
        <img
          class="provisie-toggle provisie-toggle"
          :src="chevronUp"
          alt="Open summary"
          :class="{ 'provisie-toggle__rotate-180': !showProductProvisie }"
          @click="showProductProvisie = !showProductProvisie"
        >
      </div>
    </FormKit>
  </div>
</template>

<style scoped lang="scss">
.summary {
  padding: 0 1.6rem;

  .coverage {
    width: 100%;
    margin: 1.6rem 0 0;

    &__name {
      font-family: var(--font-medium);
      font-weight: 700;
    }

    &__logo {
      width: 15%;
      padding-bottom: 1.6rem;
      max-height: 1.7rem;
    }

    &__type {
      font-size: 2rem;
      font-family: var(--font-medium);
      font-weight: 700;
    }
  }

  .totals {
    width: 100%;

    &__amount {
      font-family: var(--font-medium);
      font-weight: 700;
      text-align: right;
      font-size: 4rem;
      color: var(--theme-color-cta-100);
    }
  }

  .discount {
    font-family: var(--font-medium);
    font-size: 1.4rem;
    color: var(--ux-colors-white);
    border-radius: 1.7rem;
    background-color: var(--theme-font-color-accent-125);
    padding: 0.7rem 1rem;
    width: max-content;
    display: flex;
    align-items: center;

    &:before {
      content: '';
      display: inline-block;
      width: 20px;
      height: 20px;
      background: url('@/assets/svg/icons/gift.svg') no-repeat;
      background-size: contain;
      margin-right: 5px;
    }
  }

  .provisie-toggle {
    cursor: pointer;

    &__rotate-180 {
      transform: rotate(180deg);
    }
  }
}
</style>
